<script>
import Phaser from 'phaser';
class GomokuScene extends Phaser.Scene {
  constructor() {
    super({key: 'GomokuScene'});
    this.gridSize = 15;
    this.cellSize = 40;
  }

  //初始化
  create() {
    this.initAI();
    this.drawBoard();
    this.resetGame();
    // 初始化棋盘状态（0=空，1=黑棋，2=白棋）
    this.board = Array(this.gridSize).fill().map(() => Array(this.gridSize).fill(0));
    this.currentPlayer = 1; // 1=黑棋先行
    // 启用输入交互
    this.input.on('pointerdown', (pointer) => {
      if (!this.gameEnded) {
        this.handleClick(pointer);
      }
    });
    // 创建当前玩家提示
    this.createPlayerIndicator();
    // 如果AI先手
    if (this.currentPlayer === this.aiPlayer) this.aiMove();
  }

  //绘制背景
  drawBoard() {
    // 绘制背景
    const rect = this.add.rectangle(
        0,
        0,
        this.gridSize * this.cellSize,
        this.gridSize * this.cellSize,
        0xF0D9B5
    ).setOrigin(0);
    this.add.rectangle(
        this.gridSize * this.cellSize,
        0,
        rect.width,
        rect.height,
        0xFFFFFF,
    ).setOrigin(0);

    // 绘制网格线
    const graphics = this.add.graphics();
    graphics.lineStyle(2, 0x000000);

    for (let i = 0; i <= this.gridSize; i++) {
      // 横线
      graphics.moveTo(0, i * this.cellSize);
      graphics.lineTo(this.gridSize * this.cellSize, i * this.cellSize);

      // 竖线
      graphics.moveTo(i * this.cellSize, 0);
      graphics.lineTo(i * this.cellSize, this.gridSize * this.cellSize);
    }
    graphics.strokePath();
  }

  //重置游戏状态
  resetGame() {
    this.board = Array(this.gridSize).fill().map(() => Array(this.gridSize).fill(0));
    this.currentPlayer = 1;
    this.gameEnded = false;
    // 清除之前的获胜文本（如果存在）
    if (this.winText) this.winText.destroy();
    if (this.playerIndicator) {
      this.playerIndicator.setVisible(true);
      this.updatePlayerIndicator();
    }
  }

  //点击时触发的事件
  handleClick(pointer) {
    // 计算点击的网格坐标
    const gridX = Math.floor(pointer.x / this.cellSize);
    const gridY = Math.floor(pointer.y / this.cellSize);

    // 检查是否有效位置
    if (
        gridX >= 0 && gridX < this.gridSize &&
        gridY >= 0 && gridY < this.gridSize &&
        this.board[gridY][gridX] === 0
    ) {
      this.placePiece(gridX, gridY);
    }
  }

  //更新棋盘状态,添加棋子
  placePiece(x, y) {
    // 更新棋盘状态
    this.board[y][x] = this.currentPlayer;

    // 绘制棋子
    const color = this.currentPlayer === 1 ? 0x000000 : 0xFFFFFF;
    const piece = this.add.circle(
        x * this.cellSize + this.cellSize / 2,
        y * this.cellSize + this.cellSize / 2,
        this.cellSize * 0.4, // 半径=格子大小的40%
        color
    )
        .setScale(0) // 初始缩放为0
        .setDepth(1); // 确保在网格线上方
    // 如果是白棋添加边框
    if (this.currentPlayer === 2) {
      piece.setStrokeStyle(2, 0x000000);
    }
    // 添加缩放动画
    this.tweens.add({
      targets: piece,
      scale: 1, // 放大到正常尺寸
      duration: 300, // 动画时长300ms
      ease: 'Back.out' // 弹性回弹效果
    });
    // 检查是否获胜
    if (this.checkWin(x, y)) {
      this.handleWin();
      return;
    }
    if (!this.gameEnded) {
      this.currentPlayer = 3 - this.currentPlayer;
      this.updatePlayerIndicator(); // 更新UI
    }
    if (!this.gameEnded && this.currentPlayer === this.aiPlayer) {
      this.aiMove();
    }
  }

  //棋子胜利条件
  checkWin(x, y) {
    const directions = [
      [1, 0],  // 水平方向 →
      [0, 1],   // 垂直方向 ↓
      [1, 1],   // 对角线 ↘
      [1, -1]   // 反对角线 ↗
    ];

    const player = this.currentPlayer;

    for (const [dx, dy] of directions) {
      let count = 1;  // 当前棋子已算1个

      // 正向检测
      count += this.countDirection(x, y, dx, dy, player);
      // 反向检测
      count += this.countDirection(x, y, -dx, -dy, player);

      if (count >= 5) return true;
    }

    return false;
  }

  countDirection(x, y, dx, dy, player) {
    let count = 0;
    for (let i = 1; i < 5; i++) { // 最多检测4个棋子
      const nx = x + i * dx;
      const ny = y + i * dy;

      // 边界检查
      if (
          nx < 0 || nx >= this.gridSize ||
          ny < 0 || ny >= this.gridSize ||
          this.board[ny][nx] !== player
      ) {
        break;
      }
      count++;
    }
    return count;
  }

  //获胜后的处理
  handleWin() {
    this.gameEnded = true;
    this.playerIndicator.setVisible(false);
    const winner = this.currentPlayer === 1 ? '黑棋' : '白棋';

    // 创建获胜文本（居中显示）
    this.winText = this.add.text(
        this.cameras.main.centerX,
        this.cameras.main.centerY,
        `${winner}获胜！\n点击重新开始`,
        {
          font: 'bold 32px Arial',
          fill: '#ff0000',
          align: 'center'
        }
    ).setOrigin(0.5);

    // 设置可点击重新开始
    this.winText.setInteractive();
    this.winText.on('pointerdown', () => {
      this.scene.restart();
    });

    this.gameEnded = true;
  }

  //当前玩家提示
  createPlayerIndicator() {
    // 提示背景
    const bg = this.add.rectangle(0, 0, 200, 80, 0xF0D9B5)
        .setStrokeStyle(2, 0x4ac304)
        .setOrigin(0.5);
    // 文本标签
    const text = this.add.text(-40, 0, '当前玩家:', {
      font: 'bold 20px Arial',
      fill: '#4f4380', // 荧光粉
      shadow: {color: '#000', offsetX: 1, offsetY: 1}
    }).setOrigin(0.5);
    // 棋子指示器
    this.currentIndicator = this.add.circle(30, 0, 18, 0x000000)
        .setStrokeStyle(2, 0x333333);
    // 添加到容器
    this.playerIndicator = this.add.container(
        this.scale.width - 250,
        50
    ).add([bg, text, this.currentIndicator]);
    // 添加发光效果
    this.tweens.add({
      targets: this.currentIndicator,
      scale: 1.1,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut'
    });
    // 初始更新
    this.updatePlayerIndicator();

  }

  //提示状态更新
  updatePlayerIndicator() {
    const color = this.currentPlayer === 1 ? 0x000000 : 0xFFFFFF;
    this.currentIndicator.setFillStyle(color);

    if (this.currentPlayer === 2) {
      this.currentIndicator.setStrokeStyle(2, 0x000000);
    } else {
      this.currentIndicator.setStrokeStyle(0);
    }
  }

  // 新属性初始化
  initAI() {
    this.aiDifficulty = 3; // 搜索深度
    this.aiPlayer = 2;     // AI执白
    this.humanPlayer = 1;  // 玩家执黑
  }

  // AI落子入口
  aiMove() {
    if (this.currentPlayer === this.aiPlayer && !this.gameEnded) {
      const [bestX, bestY] = this.findBestMove();
      this.time.delayedCall(500, () => { // 延迟500ms模拟思考
        this.placePiece(bestX, bestY);
      });
    }
  }
  // 查找最佳落子
  findBestMove() {
    const candidates = this.getRelevantPositions();
    let bestScore = -Infinity;
    let bestMove = candidates[0]; // 默认第一个候选位置

    for (const [x, y] of candidates) {
      this.board[y][x] = this.aiPlayer;
      const score = this.minimax(0, false, -Infinity, Infinity);
      this.board[y][x] = 0; // 撤销

      if (score > bestScore) {
        bestScore = score;
        bestMove = [x, y];
      }
    }

    return bestMove;
  }
  // 评估函数 - 根据棋型打分

  evaluateBoard() {
    let score = 0;
    const directions = [
      [1, 0],  // 水平 →
      [0, 1],  // 垂直 ↓
      [1, 1],  // 对角线 ↘
      [1, -1]  // 反对角线 ↗
    ];

    // 扫描整个棋盘
    for (let y = 0; y < this.gridSize; y++) {
      for (let x = 0; x < this.gridSize; x++) {
        for (const dir of directions) {
          const seq = this.getSequence(x, y, dir);
          score += this.evaluateSequence(seq);
        }
      }
    }
    return score;
  }
  evaluateSequence(sequence) {
    const str = sequence.join('');

    // AI方得分模式
    const aiPatterns = {
      '11111': 100000,  // 五连
      '011110': 10000,  // 活四
      '01110': 1000,    // 活三
      '0110': 100       // 活二
    };

    // 玩家方威胁模式（负分）
    const playerPatterns = {
      '22222': -80000,
      '022220': -8000,
      '02220': -800
    };

    // 检查所有模式
    for (const [pattern, value] of Object.entries(aiPatterns)) {
      if (str.includes(pattern)) return value;
    }
    for (const [pattern, value] of Object.entries(playerPatterns)) {
      if (str.includes(pattern)) return value;
    }

    return 0;
  }
  // 带α-β剪枝的Minimax
  minimax(depth, isMaximizing, alpha, beta) {
    // 终止条件：达到深度或游戏结束
    const score = this.evaluateBoard();
    if (depth === this.aiDifficulty || score !== 0) {
      return score;
    }
    if (isMaximizing) {
      let maxEval = -Infinity;
      for (let y = 0; y < this.gridSize; y++) {
        for (let x = 0; x < this.gridSize; x++) {
          if (this.board[y][x] === 0) {
            this.board[y][x] = this.aiPlayer;
            const eval_trs = this.minimax(depth + 1, false, alpha, beta);
            this.board[y][x] = 0;
            maxEval = Math.max(maxEval, eval_trs);
            alpha = Math.max(alpha, eval_trs);
            if (beta <= alpha) break; // α-β剪枝
          }
        }
      }
      return maxEval;
    } else {
      // 最小化部分（类似上面，取最小值）
    }
  }

  getSequence(x, y, direction, length = 5) {
    const [dx, dy] = direction;
    const sequence = [];

    for (let i = 0; i < length; i++) {
      const nx = x + i * dx;
      const ny = y + i * dy;

      // 边界检查
      if (nx < 0 || nx >= this.gridSize || ny < 0 || ny >= this.gridSize) {
        sequence.push(-1); // 超出边界标记为-1
      } else {
        sequence.push(this.board[ny][nx]); // 获取棋子类型
      }
    }

    return sequence;
  }

  getRelevantPositions() {
    const positions = [];
    // 只扫描棋盘上有棋子相邻的空位
    for (let y = 0; y < this.gridSize; y++) {
      for (let x = 0; x < this.gridSize; x++) {
        if (this.board[y][x] === 0 && this.hasNeighbor(x, y)) {
          positions.push([x, y]);
        }
      }
    }
    return positions.length ? positions : [[7, 7]]; // 默认中心位置
  }

  hasNeighbor(x, y, distance = 2) {
    for (let dy = -distance; dy <= distance; dy++) {
      for (let dx = -distance; dx <= distance; dx++) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx >= 0 && nx < this.gridSize &&
            ny >= 0 && ny < this.gridSize &&
            this.board[ny][nx] !== 0) {
          return true;
        }
      }
    }
    return false;
  }
}
export default {
  name: 'GomokuGame',
  data() {
    return {
      game: null
    };
  },
  mounted() {
    this.initGame();
  },
  beforeUnmount() {
    if (this.game) {
      this.game.destroy(true);
    }
  },
  methods: {
    initGame() {
      const config = {
        type: Phaser.AUTO,
        parent: 'game-container',
        width: 15 * 40 + 400, // 直接计算好的尺寸
        height: 15 * 40,
        scene: [GomokuScene] // 使用我们定义的场景类
      };

      this.game = new Phaser.Game(config);
    }
  }
};
</script>

<template>
  <div id="game-container"></div>
</template>

<style scoped>
#game-container {
  display: flex;
  justify-content: center;
  margin: 0 auto;
  background-color: white;
}
</style>
